import { useEffect, useState } from 'react'
import { api, type AccountErpReadWindowForm, type AccountErpReadWindowView } from '../lib/api.ts'
import { realignmentNotice } from '../lib/scheduleRealignment.ts'
import { TIME_ZONE_LABEL } from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { EntityForm } from './EntityForm.tsx'
import { Input } from './Input.tsx'
import { useToast } from './Toast.tsx'

/**
 * When this account's ERP may be read (#675), on the account's page beside its
 * time zone and saved apart from it: two times on the account's own clock, or
 * none to read at any hour, as before there was a choice.
 */
export function AccountErpReadWindow({ accountId }: { accountId: string }) {
  const [view, setView] = useState<AccountErpReadWindowView | undefined>()
  const [form, setForm] = useState<AccountErpReadWindowForm | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  function take(next: AccountErpReadWindowView) {
    setView(next)
    setForm(formOf(next))
  }

  useEffect(() => {
    void api
      .accountErpReadWindow(accountId)
      .then(take)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [accountId])

  if (error !== undefined) return <p className="text-tP text-text-error">{error}</p>
  if (view === undefined || form === undefined) return null

  const dirty = JSON.stringify(form) !== JSON.stringify(formOf(view))

  async function save() {
    if (form === undefined) return
    setSaving(true)
    try {
      const saved = await api.saveAccountErpReadWindow(accountId, form)
      take(saved)
      const notice = realignmentNotice('Janela de leitura do ERP salva', saved.schedules)
      toast.show(notice.message, { tone: notice.tone, detail: notice.detail })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <EntityForm
      title="Janela de leitura do ERP"
      description="Os horários em que o ERP desta conta pode ser lido, no fuso da conta, escolhido em Dados. Fora deles, a descoberta de requisições, a varredura das etapas e as sincronizações do catálogo e das filiais não tentam o ERP. O que uma pessoa pede na hora não é bloqueado: o teste da conexão, a busca de uma requisição, o Rodar agora da tela Rotinas."
      onSave={() => void save()}
      saving={saving}
      dirty={dirty}
    >
      <Input
        type="time"
        label="Pode ler a partir das"
        value={form.start}
        onChange={(event) => setForm({ ...form, start: event.target.value })}
      />
      <Input
        type="time"
        label="Até as"
        value={form.end}
        helperText="Pode ser antes do início: 22:00 até 06:00 atravessa a meia-noite."
        onChange={(event) => setForm({ ...form, end: event.target.value })}
      />
      <div className="flex flex-col gap-2 md:col-span-2">
        <ErpReadWindowStatus view={view} />
        <div>
          <Button
            type="button"
            variant="secondary"
            tone="neutral"
            size="small"
            disabled={form.start === '' && form.end === ''}
            onClick={() => setForm({ start: '', end: '' })}
          >
            Ler a qualquer hora
          </Button>
        </div>
      </div>
    </EntityForm>
  )
}

/** What the saved window means now, on the account's clock. */
export function ErpReadWindowStatus({ view }: { view: AccountErpReadWindowView }) {
  const sweep = `A sincronização completa do catálogo roda às ${view.catalogSweepAt}.`
  if (view.start === null || view.end === null) {
    return (
      <p className="text-tSmall text-text-weak">
        Sem janela: o ERP desta conta é lido a qualquer hora. {sweep}
      </p>
    )
  }
  return (
    <p className="text-tSmall text-text-weak">
      O ERP é lido das {view.start} às {view.end}, no fuso {TIME_ZONE_LABEL[view.timeZone]}.{' '}
      {view.readableNow ? (
        'Agora ele pode ser lido.'
      ) : (
        <strong className="text-text-strong">Agora está fora da janela: o ERP não é lido.</strong>
      )}{' '}
      {sweep}
    </p>
  )
}

/** The form as the saved window fills it; empty ends for no window. */
function formOf(view: AccountErpReadWindowView): AccountErpReadWindowForm {
  return { start: view.start ?? '', end: view.end ?? '' }
}
